import React, { useEffect, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { budgetAPI } from '../services/api';
import SankeyChart from '../components/SankeyChart';

ChartJS.register(ArcElement, Tooltip, Legend);

interface BudgetItem {
  id: number;
  priority: string;
  category: string;
  name: string;
  monthly_amount: number;
  is_active: boolean;
  notes?: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
  }).format(value);
};

// Category colors for Sankey chart
const categoryColors: Record<string, string> = {
  Housing: '#ef4444',
  Food: '#f59e0b',
  Transport: '#3b82f6',
  Utilities: '#6366f1',
  Insurance: '#64748b',
  Healthcare: '#ec4899',
  Entertainment: '#f472b6',
  Shopping: '#fb923c',
  Savings: '#10b981',
  Debt: '#dc2626',
  Other: '#78716c',
  Personal: '#fb923c',
};

// Smart categorization rules based on keywords
const categoryKeywords: Record<string, string[]> = {
  Housing: ['rent', 'mortgage', 'property', 'condo', 'apartment', 'house', 'hoa', 'strata', 'maintenance fee', 'home'],
  Food: ['grocery', 'groceries', 'food', 'restaurant', 'dining', 'takeout', 'coffee', 'lunch', 'dinner', 'breakfast', 'meal', 'costco', 'walmart', 'superstore', 'loblaws', 'metro', 'sobeys', 'no frills'],
  Transport: ['car', 'gas', 'fuel', 'transit', 'bus', 'uber', 'lyft', 'taxi', 'parking', 'auto', 'vehicle', 'presto', 'compass', 'insurance car', 'car insurance', 'metro pass', 'train'],
  Utilities: ['hydro', 'electric', 'electricity', 'water', 'gas bill', 'internet', 'wifi', 'phone', 'mobile', 'cell', 'cable', 'telus', 'rogers', 'bell', 'fido', 'koodo', 'freedom', 'shaw', 'enbridge', 'fortis'],
  Insurance: ['insurance', 'life insurance', 'health insurance', 'home insurance', 'tenant', 'renter'],
  Healthcare: ['health', 'medical', 'doctor', 'dentist', 'dental', 'prescription', 'pharmacy', 'therapy', 'therapist', 'counseling', 'vision', 'glasses', 'contacts', 'gym', 'fitness'],
  Entertainment: ['netflix', 'spotify', 'disney', 'hulu', 'amazon prime', 'youtube', 'streaming', 'gaming', 'playstation', 'xbox', 'nintendo', 'movie', 'concert', 'subscription', 'apple music', 'hbo'],
  Shopping: ['amazon', 'shopping', 'clothes', 'clothing', 'shoes', 'electronics', 'furniture', 'ikea', 'best buy', 'home depot'],
  Personal: ['haircut', 'salon', 'spa', 'personal care', 'grooming', 'cosmetics', 'makeup', 'skincare'],
  Savings: ['savings', 'investment', 'rrsp', 'tfsa', 'resp', 'retirement', 'emergency fund', 'rainy day'],
  Debt: ['loan', 'credit card', 'debt', 'payment', 'student loan', 'line of credit', 'loc', 'visa', 'mastercard', 'amex'],
};

// Smart categorization function
const suggestCategory = (name: string): string | null => {
  const lowerName = name.toLowerCase();
  for (const [category, keywords] of Object.entries(categoryKeywords)) {
    for (const keyword of keywords) {
      if (lowerName.includes(keyword)) {
        return category;
      }
    }
  }
  return null;
};

const Budget: React.FC = () => {
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<BudgetItem | null>(null);
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [monthlyIncome, setMonthlyIncome] = useState<number>(() => {
    const saved = localStorage.getItem('monthlyIncome');
    return saved ? parseFloat(saved) : 12000;
  });
  const [editingIncome, setEditingIncome] = useState(false);
  const [incomeInput, setIncomeInput] = useState(monthlyIncome.toString());
  const [formData, setFormData] = useState({
    priority: '',
    category: '',
    name: '',
    monthly_amount: '',
    notes: '',
  });

  const priorities = ['Critical', 'Important', 'Luxury'];
  const categories = ['Housing', 'Food', 'Transport', 'Utilities', 'Insurance', 'Healthcare', 'Entertainment', 'Shopping', 'Personal', 'Savings', 'Debt', 'Other'];

  const priorityColors: Record<string, string> = {
    Critical: '#ef4444',
    Important: '#f59e0b',
    Luxury: '#8b5cf6',
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await budgetAPI.getAll();
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch budget items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIncomeUpdate = () => {
    const newIncome = parseFloat(incomeInput) || 0;
    setMonthlyIncome(newIncome);
    localStorage.setItem('monthlyIncome', newIncome.toString());
    setEditingIncome(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        priority: formData.priority,
        category: formData.category,
        name: formData.name,
        monthly_amount: parseFloat(formData.monthly_amount),
        notes: formData.notes,
      };

      if (editingItem) {
        await budgetAPI.update(editingItem.id, data);
      } else {
        await budgetAPI.create(data);
      }

      setShowModal(false);
      setEditingItem(null);
      setFormData({ priority: '', category: '', name: '', monthly_amount: '', notes: '' });
      fetchItems();
    } catch (error) {
      console.error('Failed to save budget item:', error);
    }
  };

  const handleEdit = (item: BudgetItem) => {
    setEditingItem(item);
    setFormData({
      priority: item.priority,
      category: item.category,
      name: item.name,
      monthly_amount: item.monthly_amount.toString(),
      notes: item.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this budget item?')) {
      try {
        await budgetAPI.delete(id);
        fetchItems();
      } catch (error) {
        console.error('Failed to delete budget item:', error);
      }
    }
  };

  // Handle name change with auto-suggest category
  const handleNameChange = (name: string) => {
    setFormData(prev => {
      const suggested = suggestCategory(name);
      // Only auto-fill if category is empty and we have a suggestion
      if (suggested && !prev.category) {
        return { ...prev, name, category: suggested };
      }
      return { ...prev, name };
    });
  };

  // Auto-categorize using keyword matching (fallback)
  const handleKeywordCategorize = async () => {
    const uncategorized = items.filter(i => !i.category || i.category === 'Uncategorized');
    if (uncategorized.length === 0) {
      alert('All items are already categorized!');
      return;
    }

    const updates: Promise<any>[] = [];
    let categorizedCount = 0;

    for (const item of uncategorized) {
      const suggested = suggestCategory(item.name);
      if (suggested) {
        updates.push(budgetAPI.update(item.id, { ...item, category: suggested }));
        categorizedCount++;
      }
    }

    if (updates.length === 0) {
      alert('Could not auto-categorize any items. Try AI categorization or categorize manually.');
      return;
    }

    try {
      await Promise.all(updates);
      alert(`Successfully categorized ${categorizedCount} items!`);
      fetchItems();
    } catch (error) {
      console.error('Failed to auto-categorize:', error);
      alert('Failed to auto-categorize some items.');
    }
  };

  // AI-powered categorization using Claude Haiku
  const handleAICategorize = async () => {
    // Include items with no category, 'Uncategorized', or invalid categories (like priority values)
    const uncategorized = items.filter(i => !i.category || i.category === 'Uncategorized' || !categories.includes(i.category));
    if (uncategorized.length === 0) {
      alert('All items are already categorized!');
      return;
    }

    setAiCategorizing(true);

    try {
      // Get AI categorizations
      const itemNames = uncategorized.map(i => i.name);
      const response = await budgetAPI.aiCategorize(itemNames);
      const categorizations = response.data.categorizations;

      // Apply categorizations
      const updates: Promise<any>[] = [];
      for (const item of uncategorized) {
        const category = categorizations[item.name];
        if (category) {
          updates.push(budgetAPI.update(item.id, { ...item, category }));
        }
      }

      await Promise.all(updates);
      alert(`AI successfully categorized ${updates.length} items!`);
      fetchItems();
    } catch (error: any) {
      console.error('AI categorization failed:', error);
      if (error.response?.status === 503) {
        alert('AI categorization not configured. Using keyword matching instead.');
        handleKeywordCategorize();
      } else {
        alert(error.response?.data?.error || 'AI categorization failed. Try again later.');
      }
    } finally {
      setAiCategorizing(false);
    }
  };

  const groupedByPriority = priorities.map((priority) => ({
    priority,
    items: items.filter((i) => i.priority === priority && i.is_active),
    total: items.filter((i) => i.priority === priority && i.is_active).reduce((sum, i) => sum + i.monthly_amount, 0),
  }));

  const totalBudget = items.filter(i => i.is_active).reduce((sum, i) => sum + i.monthly_amount, 0);
  const monthlySavings = monthlyIncome - totalBudget;
  const savingsRate = monthlyIncome > 0 ? (monthlySavings / monthlyIncome) * 100 : 0;

  // Group by category for Sankey chart - dynamically discover categories from data
  const activeItems = items.filter(i => i.is_active);
  const uniqueCategories = Array.from(new Set(activeItems.map(i => i.category || 'Uncategorized')));
  const GROUPING_THRESHOLD = 100; // Categories under $100 grouped into "Other"

  const rawTotals = uniqueCategories.map(cat => ({
    category: cat,
    total: activeItems.filter(i => (i.category || 'Uncategorized') === cat).reduce((sum, i) => sum + i.monthly_amount, 0),
    color: categoryColors[cat] || '#64748b',
  })).filter(c => c.total > 0);

  // Separate large and small categories
  const largeCats = rawTotals.filter(c => c.total >= GROUPING_THRESHOLD);
  const smallCats = rawTotals.filter(c => c.total < GROUPING_THRESHOLD);
  const otherTotal = smallCats.reduce((sum, c) => sum + c.total, 0);

  // Combine into final totals - group small categories into "Other"
  const categoryTotals = otherTotal > 0
    ? [...largeCats, { category: 'Other', total: otherTotal, color: '#78716c' }]
    : largeCats;

  const hasGroupedCategories = smallCats.length > 0;

  // Auto-scale chart height based on category count
  const categoryCount = categoryTotals.length + 1; // +1 for savings
  const minChartHeight = 300;
  const heightPerCategory = 90;
  const chartHeight = Math.max(minChartHeight, categoryCount * heightPerCategory);

  const chartData = {
    labels: priorities,
    datasets: [{
      data: groupedByPriority.map((g) => g.total),
      backgroundColor: priorities.map((p) => priorityColors[p]),
      borderWidth: 0,
    }],
  };

  if (loading) {
    return <div>Loading budget...</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Budget</h1>
          <p className="page-subtitle">Manage your monthly spending</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {items.some(i => !i.category || i.category === 'Uncategorized' || !categories.includes(i.category)) && (
            <button
              className="btn btn-secondary"
              onClick={handleAICategorize}
              disabled={aiCategorizing}
              title="Use AI to categorize uncategorized items"
            >
              {aiCategorizing ? 'Categorizing...' : 'AI Categorize'}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Budget Item
          </button>
        </div>
      </div>

      {/* Summary Cards - Responsive */}
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {/* Monthly Income */}
        <div className="card" style={{ padding: '1.25rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Monthly Income
          </div>
          {editingIncome ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="number"
                className="form-input"
                value={incomeInput}
                onChange={(e) => setIncomeInput(e.target.value)}
                style={{ fontSize: '1.25rem', padding: '0.25rem 0.5rem' }}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleIncomeUpdate()}
              />
              <button className="btn btn-primary btn-sm" onClick={handleIncomeUpdate}>Save</button>
            </div>
          ) : (
            <div
              style={{ fontSize: '1.75rem', fontWeight: 700, cursor: 'pointer' }}
              onClick={() => { setIncomeInput(monthlyIncome.toString()); setEditingIncome(true); }}
              title="Click to edit"
            >
              {formatCurrency(monthlyIncome)}
            </div>
          )}
        </div>

        {/* Total Budget */}
        <div className="card" style={{ padding: '1.25rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Total Budget
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>
            {formatCurrency(totalBudget)}
          </div>
        </div>

        {/* Monthly Savings */}
        <div className="card" style={{
          padding: '1.25rem',
          background: monthlySavings >= 0 ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white'
        }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem', opacity: 0.9 }}>
            Monthly Savings
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>
            {formatCurrency(monthlySavings)}
          </div>
        </div>

        {/* Savings Rate */}
        <div className="card" style={{ padding: '1.25rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Savings Rate
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: savingsRate >= 20 ? '#10b981' : savingsRate >= 0 ? '#f59e0b' : '#ef4444' }}>
            {savingsRate.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Mobile: Swipeable Category Cards */}
      <div className="mobile-only" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-3)' }}>Budget Breakdown</h3>
        <div className="swipe-container">
          {/* Savings Card */}
          {monthlySavings > 0 && (
            <div className="swipe-card">
              <div className="swipe-card-header">
                <div className="swipe-card-icon" style={{ backgroundColor: '#10b981' }} />
                <span className="swipe-card-title">Savings</span>
              </div>
              <div className="swipe-card-value" style={{ color: '#10b981' }}>
                {formatCurrency(monthlySavings)}
              </div>
              <div className="swipe-card-percent">
                {((monthlySavings / monthlyIncome) * 100).toFixed(1)}% of income
              </div>
              <div className="swipe-card-bar">
                <div
                  className="swipe-card-bar-fill"
                  style={{
                    width: `${Math.min((monthlySavings / monthlyIncome) * 100, 100)}%`,
                    backgroundColor: '#10b981'
                  }}
                />
              </div>
            </div>
          )}
          {/* Category Cards */}
          {categoryTotals.map((cat) => (
            <div key={cat.category} className="swipe-card">
              <div className="swipe-card-header">
                <div className="swipe-card-icon" style={{ backgroundColor: cat.color }} />
                <span className="swipe-card-title">{cat.category}</span>
              </div>
              <div className="swipe-card-value">
                {formatCurrency(cat.total)}
              </div>
              <div className="swipe-card-percent">
                {((cat.total / monthlyIncome) * 100).toFixed(1)}% of income
              </div>
              <div className="swipe-card-bar">
                <div
                  className="swipe-card-bar-fill"
                  style={{
                    width: `${Math.min((cat.total / monthlyIncome) * 100, 100)}%`,
                    backgroundColor: cat.color
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        {hasGroupedCategories && (
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            textAlign: 'center',
            marginTop: 'var(--space-2)',
            fontStyle: 'italic'
          }}>
            * Categories under $100 grouped into "Other"
          </p>
        )}
      </div>

      {/* Desktop: Budget Flow Sankey Chart */}
      <div className="desktop-only">
        <div className="card" style={{ marginBottom: '1.5rem', overflow: 'hidden' }}>
          <div className="card-header">
            <h3 className="card-title">Budget Flow</h3>
          </div>
          <div style={{
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-primary)',
            minHeight: `${chartHeight + 40}px`
          }}>
            <SankeyChart
              income={monthlyIncome}
              categoryTotals={categoryTotals}
              width={850}
              height={chartHeight}
            />
            {hasGroupedCategories && (
              <p style={{
                fontSize: '0.75rem',
                color: '#64748b',
                textAlign: 'center',
                marginTop: '0.5rem',
                fontStyle: 'italic'
              }}>
                * Categories under $100 are grouped into "Other"
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Budget Items by Priority - Responsive Layout */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Budget Items</h3>
        </div>
        <div className="grid-3" style={{ padding: 'var(--space-4)' }}>
          {groupedByPriority.map((group) => (
            <div key={group.priority}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                background: `${priorityColors[group.priority]}15`,
                borderRadius: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: priorityColors[group.priority]
                  }} />
                  {group.priority}
                </span>
                <span style={{ fontWeight: 600, color: priorityColors[group.priority] }}>
                  {formatCurrency(group.total)}
                </span>
              </div>
              {group.items.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {group.items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem 0.75rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ fontWeight: 500 }}>{item.name}</div>
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '9999px',
                          backgroundColor: `${categoryColors[item.category] || '#64748b'}20`,
                          color: categoryColors[item.category] || '#64748b',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          {item.category || 'Uncategorized'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: 500 }}>{formatCurrency(item.monthly_amount)}</span>
                        <button
                          className="btn btn-sm"
                          style={{ padding: '0.25rem', background: 'transparent', color: 'var(--text-secondary)' }}
                          onClick={() => handleEdit(item)}
                        >
                          ✎
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ padding: '0.25rem', background: 'transparent', color: '#ef4444' }}
                          onClick={() => handleDelete(item.id)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                  No items yet
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingItem ? 'Edit Budget Item' : 'Add Budget Item'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="form-input"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  required
                >
                  <option value="">Select priority</option>
                  {priorities.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Rent, Groceries (will auto-suggest category)"
                  required
                />
                {formData.name && suggestCategory(formData.name) && !formData.category && (
                  <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>
                    Suggested: {suggestCategory(formData.name)}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Monthly Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.monthly_amount}
                  onChange={(e) => setFormData({ ...formData, monthly_amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea
                  className="form-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update' : 'Add'} Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
